import React from "react";
import { C } from "../theme";
import { fmt, fmtD, Tag, Kpi, Btn, AdBanner, getAppCopy } from "../shared";
import { IMMEUBLES, chartMensuel, chartCharges } from "../data";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

export const Dashboard = ({ residents, cotisations, charges, immId, pendingCount = 0, setPage, locale = "fr", maintenance = [] }) => {
  const copy = getAppCopy(locale);
  const res = residents.filter(r => r.immId === immId);
  const cots = cotisations.filter(c => c.immId === immId);
  const chgs = charges.filter(c => c.immId === immId);
  const imm = IMMEUBLES.find(i => i.id === immId);
  const totalDu = cots.filter(c => c.statut !== "payé").reduce((a,c) => a + c.montant, 0);
  const totalRecu = cots.filter(c => c.statut === "payé").reduce((a,c) => a + c.montant, 0);
  const totalCharges = chgs.reduce((a,c) => a + c.montant, 0);
  const retardaires = res.filter(r => r.statut === "débiteur").length;

  return (
    <div>
      <div style={{marginBottom:22}}>
        <h2 style={{fontSize:25,fontWeight:800,color:C.text}}>{imm?.nom}</h2>
        <p style={{color:C.muted,fontSize:13,marginTop:3}}>{imm?.adresse} · {imm?.lots} lots · Mars 2024</p>
      </div>
      {pendingCount > 0 && (
        <div className="fu" style={{background:`linear-gradient(135deg,${C.gold}22,${C.gold}11)`,border:`1.5px solid ${C.gold}66`,borderRadius:14,padding:"14px 20px",marginBottom:20,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <span style={{fontSize:22}}>🔔</span>
            <div>
              <p style={{fontWeight:700,color:C.text,fontSize:14}}>{pendingCount} demande{pendingCount>1?"s":""} d'inscription en attente</p>
              <p style={{fontSize:12,color:C.muted}}>Des copropriétaires ont rejoint RésiFlow et attendent votre validation.</p>
            </div>
          </div>
          <button className="btn" onClick={() => setPage("residents")}
            style={{padding:"9px 18px",borderRadius:10,background:C.gold,color:"#fff",fontWeight:700,fontSize:13}}>
            Voir les demandes →
          </button>
        </div>
      )}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:22}}>
        <Kpi icon="💰" label={copy.receivedFees} value={fmt(totalRecu)} color={C.green} delay={0} />
        <Kpi icon="⏳" label={copy.dueAmount} value={fmt(totalDu)} sub={`${cots.filter(c=>c.statut!=="payé").length} ${copy.waiting}`} color={C.gold} delay={60} />
        <Kpi icon="💸" label={copy.totalCharges} value={fmt(totalCharges)} color={C.blue} delay={120} />
        <Kpi icon="⚠️" label={copy.latePayers} value={retardaires} sub={`/${res.length} ${copy.residents.toLowerCase()}`} color={C.red} delay={180} />
      </div>

      {/* Trésorerie Forecast */}
      <div className="fu" style={{animationDelay:"100ms",background:`linear-gradient(135deg,${C.navy},#1e3080)`,borderRadius:16,padding:"18px 22px",marginBottom:18,display:"flex",alignItems:"center",justifyContent:"space-between",boxShadow:"0 6px 28px rgba(11,19,51,.14)"}}>
        <div style={{display:"flex",alignItems:"center",gap:14}}>
          <div style={{width:44,height:44,borderRadius:12,background:"rgba(255,255,255,.1)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>🏦</div>
          <div>
            <p style={{color:"rgba(255,255,255,.5)",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:".5px",marginBottom:3}}>Prévision trésorerie — T2 2024</p>
            <p style={{color:"#fff",fontSize:20,fontWeight:800,fontFamily:"Syne"}}>{fmt(totalRecu - totalCharges)}</p>
            <p style={{color:"rgba(255,255,255,.4)",fontSize:12,marginTop:2}}>Taux recouvrement : <strong style={{color:C.teal}}>{Math.round((totalRecu/(totalRecu+totalDu))*100)}%</strong> · Objectif 90%</p>
          </div>
        </div>
        <div style={{display:"flex",gap:14}}>
          {[["Attendu",fmt(totalRecu+totalDu),C.gold],["Encaissé",fmt(totalRecu),C.teal],["Charges",fmt(totalCharges),"#ff9090"]].map(([l,v,cl]) => (
            <div key={l} style={{textAlign:"center"}}>
              <p style={{color:"rgba(255,255,255,.35)",fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:".4px",marginBottom:4}}>{l}</p>
              <p style={{color:cl,fontSize:14,fontWeight:800,fontFamily:"Syne"}}>{v}</p>
            </div>
          ))}
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:18,marginBottom:18}}>
        <div className="fu" style={{animationDelay:"200ms",background:C.card,borderRadius:16,padding:22,boxShadow:"0 2px 14px rgba(11,19,51,.06)"}}>
          <h4 style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:16}}>Cotisations — Encaissé vs Prévu</h4>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartMensuel} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e8ecf5" />
              <XAxis dataKey="mois" tick={{fontSize:11,fill:C.muted}} axisLine={false} tickLine={false} />
              <YAxis tick={{fontSize:10,fill:C.muted}} axisLine={false} tickLine={false} tickFormatter={v => `${v/1000}k`} />
              <Tooltip formatter={v => fmt(v)} contentStyle={{borderRadius:10,border:"none"}} />
              <Bar dataKey="prev" fill="#e8ecf5" radius={[5,5,0,0]} />
              <Bar dataKey="enc" fill={C.teal} radius={[5,5,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="fu" style={{animationDelay:"240ms",background:C.card,borderRadius:16,padding:22,boxShadow:"0 2px 14px rgba(11,19,51,.06)"}}>
          <h4 style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:16}}>{copy.chargesSplit}</h4>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={chartCharges} cx="50%" cy="50%" outerRadius={78} innerRadius={44} dataKey="value" paddingAngle={3}>
                {chartCharges.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip formatter={v => fmt(v)} contentStyle={{borderRadius:10,border:"none"}} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{display:"flex",flexWrap:"wrap",gap:8,justifyContent:"center",marginTop:4}}>
            {chartCharges.map(c => (
              <div key={c.name} style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:C.muted}}>
                <div style={{width:9,height:9,borderRadius:2,background:c.color}} />{c.name}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Maintenance urgente widget */}
      {maintenance.filter(m => m.immId === immId && m.statut !== "Résolu").length > 0 && (
        <div className="fu" style={{animationDelay:"260ms",background:C.card,borderRadius:16,padding:20,boxShadow:"0 2px 14px rgba(11,19,51,.06)",marginBottom:18}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <h4 style={{fontSize:14,fontWeight:700,color:C.text}}>🛠️ Maintenance en cours</h4>
            <button className="btn" onClick={() => setPage("maintenance")}
              style={{padding:"5px 13px",background:C.blueL,color:C.blue,borderRadius:9,fontWeight:600,fontSize:12}}>
              Voir tout →
            </button>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {maintenance.filter(m => m.immId === immId && m.statut !== "Résolu").slice(0,3).map(m => {
              const pc = m.priorite==="critique"?C.red:m.priorite==="urgent"?C.gold:C.blue;
              return (
                <div key={m.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",borderRadius:10,background:C.bg,border:`1px solid ${C.border}`}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <div style={{width:8,height:8,borderRadius:"50%",background:pc,flexShrink:0}} />
                    <span style={{fontSize:13,fontWeight:600,color:C.text}}>{m.titre}</span>
                    <span style={{fontSize:11,color:C.muted}}>{m.cat}</span>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:11,fontWeight:600,color:pc}}>{m.priorite}</span>
                    <span style={{background:m.statut==="En cours"?C.blueL:"#fffbeb",color:m.statut==="En cours"?C.blue:C.gold,padding:"2px 8px",borderRadius:6,fontSize:10,fontWeight:700}}>{m.statut}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <AdBanner />

      <div className="fu" style={{animationDelay:"270ms",background:C.card,borderRadius:16,padding:22,boxShadow:"0 2px 14px rgba(11,19,51,.06)"}}>
        <h4 style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:16}}>{copy.recentActivity}</h4>
        {cots.slice(0,4).map((c, i) => (
          <div key={c.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"11px 0",borderBottom:i<3?`1px solid ${C.border}`:"none"}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <div style={{width:36,height:36,borderRadius:10,background:C.tealL,display:"flex",alignItems:"center",justifyContent:"center",fontSize:17}}>
                {c.statut === "payé" ? "✅" : "📋"}
              </div>
              <div>
                <p style={{fontSize:14,fontWeight:600,color:C.text}}>{c.resident}</p>
                <p style={{fontSize:12,color:C.muted}}>{c.type} · {fmtD(c.echeance)}</p>
              </div>
            </div>
            <div style={{textAlign:"right"}}>
              <p style={{fontSize:14,fontWeight:700,color:C.text}}>{fmt(c.montant)}</p>
              <Tag s={c.statut} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════
   SYNDIC — COPROPRIÉTAIRES
══════════════════════════════════════ */
